/**
 * Score History Store
 *
 * Zustand store for tracking score history over time.
 * Captures snapshots when AI scoring is triggered, allowing tracking of
 * score changes and attribution to specific documents.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getStorage } from '@/lib/state/ssr-storage';

// ============================================================================
// Types
// ============================================================================

export type ScoreTriggerType = 'document_added' | 'document_removed' | 'profile_updated' | 'manual' | 'onboarding';
export type ReadinessLevel = 'critical' | 'weak' | 'moderate' | 'strong' | 'excellent';

export interface DimensionChange {
  dimension: string;
  score: number;
  previousScore?: number;
  change: number; // positive = improvement, negative = decline
}

export interface ScoreTrigger {
  type: ScoreTriggerType;
  documentId?: string;
  documentName?: string;
  documentType?: string;
}

export interface ScoreSnapshot {
  id: string;
  timestamp: Date;
  finalScore: number;
  dimensions: DimensionChange[];
  trigger: ScoreTrigger;
  readinessLevel: ReadinessLevel;
}

// Serialized version for persistence (Date becomes string)
interface SerializedScoreSnapshot extends Omit<ScoreSnapshot, 'timestamp'> {
  timestamp: string;
}

// ============================================================================
// Store Types
// ============================================================================

interface ScoreHistoryState {
  // State
  snapshots: ScoreSnapshot[];
  lastScoredAt: Date | null;
  isScoring: boolean;
  scoringError: string | null;

  // Actions
  addSnapshot: (snapshot: Omit<ScoreSnapshot, 'id'>) => void;
  getScoreChange: (dimension: string) => number;
  getTotalChange: () => number;
  getRecentChanges: (limit?: number) => ScoreSnapshot[];
  clearHistory: () => void;
  setIsScoring: (isScoring: boolean) => void;
  setScoringError: (error: string | null) => void;

  // Helpers
  getLatestSnapshot: () => ScoreSnapshot | null;
  getPreviousSnapshot: () => ScoreSnapshot | null;
  getDimensionHistory: (dimension: string, limit?: number) => Array<{ timestamp: Date; score: number }>;
  getImprovingDimensions: () => DimensionChange[];
  getDecliningDimensions: () => DimensionChange[];

  // Reset
  reset: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const MAX_SNAPSHOTS = 50; // Maximum number of snapshots to persist

// ============================================================================
// Helper Functions
// ============================================================================

function generateSnapshotId(): string {
  return `snap_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function getReadinessLevel(score: number): ReadinessLevel {
  if (score >= 81) return 'excellent';
  if (score >= 66) return 'strong';
  if (score >= 46) return 'moderate';
  if (score >= 26) return 'weak';
  return 'critical';
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  snapshots: [] as ScoreSnapshot[],
  lastScoredAt: null as Date | null,
  isScoring: false,
  scoringError: null as string | null,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useScoreHistoryStore = create<ScoreHistoryState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========================================================================
      // Actions
      // ========================================================================

      addSnapshot: (snapshotData: Omit<ScoreSnapshot, 'id'>) => {
        const { snapshots } = get();
        const previousSnapshot = snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;

        // Calculate dimension changes relative to previous snapshot
        const dimensions = snapshotData.dimensions.map((dim) => {
          const prevDim = previousSnapshot?.dimensions.find((d) => d.dimension === dim.dimension);
          const previousScore = prevDim?.score ?? dim.score;
          return {
            ...dim,
            previousScore,
            change: dim.score - previousScore,
          };
        });

        const newSnapshot: ScoreSnapshot = {
          ...snapshotData,
          id: generateSnapshotId(),
          dimensions,
          readinessLevel: snapshotData.readinessLevel || getReadinessLevel(snapshotData.finalScore),
        };

        // Maintain max snapshots limit
        const updatedSnapshots = [...snapshots, newSnapshot];
        const trimmedSnapshots =
          updatedSnapshots.length > MAX_SNAPSHOTS
            ? updatedSnapshots.slice(-MAX_SNAPSHOTS)
            : updatedSnapshots;

        set({
          snapshots: trimmedSnapshots,
          lastScoredAt: newSnapshot.timestamp,
          scoringError: null,
        });
      },

      getScoreChange: (dimension: string): number => {
        const { snapshots } = get();
        if (snapshots.length < 2) return 0;

        const latest = snapshots[snapshots.length - 1];
        const latestDim = latest.dimensions.find((d) => d.dimension === dimension);

        return latestDim?.change ?? 0;
      },

      getTotalChange: (): number => {
        const { snapshots } = get();
        if (snapshots.length < 2) return 0;

        const first = snapshots[0];
        const latest = snapshots[snapshots.length - 1];

        return latest.finalScore - first.finalScore;
      },

      getRecentChanges: (limit = 10): ScoreSnapshot[] => {
        const { snapshots } = get();
        return snapshots.slice(-limit).reverse(); // Most recent first
      },

      clearHistory: () => {
        set({
          snapshots: [],
          lastScoredAt: null,
          scoringError: null,
        });
      },

      setIsScoring: (isScoring: boolean) => set({ isScoring }),

      setScoringError: (error: string | null) =>
        set({
          scoringError: error,
          isScoring: false,
        }),

      // ========================================================================
      // Helpers
      // ========================================================================

      getLatestSnapshot: (): ScoreSnapshot | null => {
        const { snapshots } = get();
        return snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
      },

      getPreviousSnapshot: (): ScoreSnapshot | null => {
        const { snapshots } = get();
        return snapshots.length > 1 ? snapshots[snapshots.length - 2] : null;
      },

      getDimensionHistory: (
        dimension: string,
        limit = 10
      ): Array<{ timestamp: Date; score: number }> => {
        const { snapshots } = get();

        return snapshots
          .slice(-limit)
          .map((snap) => {
            const dim = snap.dimensions.find((d) => d.dimension === dimension);
            return dim ? { timestamp: snap.timestamp, score: dim.score } : null;
          })
          .filter((item): item is { timestamp: Date; score: number } => item !== null);
      },

      getImprovingDimensions: (): DimensionChange[] => {
        const { snapshots } = get();
        if (snapshots.length === 0) return [];

        const latest = snapshots[snapshots.length - 1];
        return latest.dimensions.filter((d) => d.change > 0).sort((a, b) => b.change - a.change);
      },

      getDecliningDimensions: (): DimensionChange[] => {
        const { snapshots } = get();
        if (snapshots.length === 0) return [];

        const latest = snapshots[snapshots.length - 1];
        return latest.dimensions.filter((d) => d.change < 0).sort((a, b) => a.change - b.change);
      },

      // ========================================================================
      // Reset
      // ========================================================================

      reset: () => set(initialState),
    }),
    {
      name: 'inverio-score-history',
      storage: createJSONStorage(getStorage),
      // Serialize Date objects to strings for storage
      partialize: (state) => ({
        snapshots: state.snapshots.map((snap) => ({
          ...snap,
          timestamp: snap.timestamp instanceof Date ? snap.timestamp.toISOString() : snap.timestamp,
        })),
        lastScoredAt: state.lastScoredAt?.toISOString() ?? null,
      }),
      // Deserialize strings back to Date objects when loading
      onRehydrateStorage: () => (state) => {
        if (state) {
          try {
            // Validate snapshots is an array before mapping
            if (Array.isArray(state.snapshots)) {
              state.snapshots = (state.snapshots as unknown as SerializedScoreSnapshot[])
                .filter((snap) => snap && typeof snap.timestamp === 'string')
                .map((snap) => ({
                  ...snap,
                  timestamp: new Date(snap.timestamp),
                }));
            } else {
              state.snapshots = [];
            }
            state.lastScoredAt = state.lastScoredAt ? new Date(state.lastScoredAt as unknown as string) : null;
          } catch {
            // Corrupted storage — reset to safe defaults
            state.snapshots = [];
            state.lastScoredAt = null;
          }
        }
      },
    }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Hook to check if scoring is currently in progress
 */
export function useIsScoring(): boolean {
  return useScoreHistoryStore((s) => s.isScoring);
}

/**
 * Hook to get the last scored timestamp
 */
export function useLastScoredAt(): Date | null {
  return useScoreHistoryStore((s) => s.lastScoredAt);
}

/**
 * Hook to get the latest snapshot
 */
export function useLatestSnapshot(): ScoreSnapshot | null {
  const snapshots = useScoreHistoryStore((s) => s.snapshots);
  return snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
}

/**
 * Hook to get score change for a specific dimension
 */
export function useDimensionChange(dimension: string): number {
  const snapshots = useScoreHistoryStore((s) => s.snapshots);
  if (snapshots.length < 2) return 0;
  const latest = snapshots[snapshots.length - 1];
  const latestDim = latest.dimensions.find((d) => d.dimension === dimension);
  return latestDim?.change ?? 0;
}

/**
 * Hook to get total score change since first snapshot
 */
export function useTotalScoreChange(): number {
  const snapshots = useScoreHistoryStore((s) => s.snapshots);
  if (snapshots.length < 2) return 0;
  const first = snapshots[0];
  const latest = snapshots[snapshots.length - 1];
  return latest.finalScore - first.finalScore;
}
